import classNames from 'classnames'
import { sortBy } from 'lodash-es'
import { FC, JSX } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import ChevronDownIcon from '~icons/bi/chevron-down'

export type SearchableSelectOption = {
	id: number | string
	name: string
}

type Props = {
	label: string
	name: string
	options: SearchableSelectOption[]
	value?: SearchableSelectOption
	onChange?: (option: SearchableSelectOption | undefined) => void
	isLoading?: boolean
	placeholder?: string
	emptyMessage?: string
	disabled?: boolean
	required?: boolean
	className?: string
}

const SearchableSelect: FC<Props> = ({
	label,
	name,
	options,
	value,
	onChange,
	isLoading,
	placeholder,
	emptyMessage,
	disabled,
	required,
	className,
}) => {
	const { t } = useTranslation('forms')
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const rootRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	const filtered = query.trim()
		? options.filter(o => o.name.toLowerCase().includes(query.trim().toLowerCase()))
		: options

	const sorted = sortBy(filtered, 'name')

	useEffect(() => {
		if (!open) return
		const onPointerDown = (e: MouseEvent) => {
			if (!rootRef.current?.contains(e.target as Node)) {
				setOpen(false)
				setQuery('')
			}
		}
		document.addEventListener('mousedown', onPointerDown)
		return () => document.removeEventListener('mousedown', onPointerDown)
	}, [open])

	const pick = (option: SearchableSelectOption) => {
		onChange?.(option)
		setOpen(false)
		setQuery('')
		inputRef.current?.blur()
	}

	const clear = () => {
		onChange?.(undefined)
		setQuery('')
	}

	const handleInput = (e: JSX.TargetedEvent<HTMLInputElement, Event>) => {
		setQuery(e.currentTarget.value)
		if (!open) setOpen(true)
		if (!e.currentTarget.value) clear()
	}

	const handleFocus = () => {
		if (disabled || isLoading) return
		setQuery(value?.name ?? '')
		setOpen(true)
	}

	const displayPlaceholder =
		placeholder ?? (t('search_or_select') as string)
	const displayEmpty = emptyMessage ?? (t('no_results') as string)

	return (
		<label class={classNames('searchable-select-field', className)}>
			<span class='searchable-select-label'>
				{label}
				{required && <span class='text-red-600'>&nbsp;*</span>}
			</span>
			<div
				ref={rootRef}
				class={classNames('searchable-select', {
					'searchable-select-open': open,
					'searchable-select-disabled': disabled || isLoading,
				})}
			>
				<div class='searchable-select-control'>
					<input
						ref={inputRef}
						type='text'
						class='searchable-select-input'
						role='combobox'
						aria-expanded={open}
						aria-autocomplete='list'
						aria-controls={`${name}-listbox`}
						disabled={disabled || isLoading}
						required={required}
						placeholder={isLoading ? (t('loading') as string) : displayPlaceholder}
						value={open ? query : (value?.name ?? '')}
						onInput={handleInput}
						onFocus={handleFocus}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								setOpen(false)
								setQuery('')
							}
							if (e.key === 'Enter' && sorted[0]) {
								e.preventDefault()
								pick(sorted[0])
							}
						}}
					/>
					<button
						type='button'
						tabIndex={-1}
						class='searchable-select-toggle'
						disabled={disabled || isLoading}
						aria-label={t('toggle_options') as string}
						onClick={() => {
							if (disabled || isLoading) return
							if (open) {
								setOpen(false)
								setQuery('')
							} else {
								setQuery(value?.name ?? '')
								setOpen(true)
								inputRef.current?.focus()
							}
						}}
					>
						<ChevronDownIcon width={14} height={14} />
					</button>
				</div>

				{open && !disabled && !isLoading && (
					<ul
						id={`${name}-listbox`}
						class='searchable-select-dropdown'
						role='listbox'
					>
						{sorted.length === 0 ? (
							<li class='searchable-select-empty' role='presentation'>
								{displayEmpty}
							</li>
						) : (
							sorted.map((option) => (
								<li
									key={option.id}
									role='option'
									aria-selected={value?.id === option.id}
									class={classNames('searchable-select-option', {
										'searchable-select-option-selected':
											value?.id === option.id,
									})}
									onMouseDown={(e) => {
										e.preventDefault()
										pick(option)
									}}
								>
									{option.name}
								</li>
							))
						)}
					</ul>
				)}

				<input type='hidden' name={name} value={value?.id ?? ''} />
			</div>
		</label>
	)
}

export default SearchableSelect
